/**
 * The Origin chests (spec docs/superpowers/specs/2026-10-13-light-orb-chests-light-shop-design.md).
 *
 * Every real win of an Origin Event level has a CHEST_CHANCE chance to drop the
 * chapter's chest onto the Storage shelf: a Dark Chest from The Dark Shadows, a
 * Light Chest from Above the Clouds. Opening one pays, at even odds, either
 * CHEST_SHARDS of the chapter's shards or CHEST_COINS of its coins.
 *
 * Both rolls take their number from the RENDER layer's unseeded random source
 * — never the sim's seeded `Rng`, whose streams the balance tests pin. They
 * are pure here, taking the number as an argument, so tests can hand one in.
 */
export type ChestKind = 'dark' | 'light';
export type ChestPrize = 'shards' | 'coins';

export const CHEST_CHANCE = 0.25;
export const CHEST_SHARDS = 2;
export const CHEST_COINS = 50;

/** Does this win drop a chest? `r` is uniform in [0, 1). */
export function rollChestDrop(r: number): boolean {
  return r < CHEST_CHANCE;
}

/** What an opened chest holds: shards below one half, coins at or above. */
export function rollChestPrize(r: number): ChestPrize {
  return r < 0.5 ? 'shards' : 'coins';
}

export const CHEST_NAMES: Record<ChestKind, string> = {
  dark: 'Dark Chest',
  light: 'Light Chest',
};

export const CHEST_ART: Record<ChestKind, string> = {
  dark: 'item-darkchest',
  light: 'item-lightchest',
};

/** "2 Dark Shards" / "50 Light Hambi Coins" — the prize as the player reads it. */
export function chestPrizeLabel(kind: ChestKind, prize: ChestPrize): string {
  const chapter = kind === 'dark' ? 'Dark' : 'Light';
  return prize === 'shards'
    ? `${CHEST_SHARDS} ${chapter} Shards`
    : `${CHEST_COINS} ${chapter} Hambi Coins`;
}

/** The art for what came out of the chest. */
export function chestPrizeArt(kind: ChestKind, prize: ChestPrize): string {
  if (prize === 'shards') return kind === 'dark' ? 'item-darkshard' : 'item-lightshard';
  return kind === 'dark' ? 'darkcoin' : 'lightcoin';
}
